/**
 * 24K.Infinity — Color tokens
 * -------------------------------------------------------------------------
 * Source of truth: "The Twin Console Widget System" reference (claude.ai
 * design project a6cfccae). Exact named swatches:
 *
 *   ELECTRIC   #1A1AF2   (primary accent)
 *   ACCENT DK  #6C6AFF   (secondary / on-dark accent)
 *   INK        #0B0B0B
 *   SURFACE    #FFFFFF
 *   SURFACE DK #121214
 *   HAIRLINE   #E6E6E2
 *   TINT       #E9E9FE   (light electric wash)
 *
 * Swiss-grade, light + dark, one electric accent. The 24K signature gold is
 * retained only as a sparing `signature`/`warning` token.
 * -------------------------------------------------------------------------
 */

// --- Brand: electric indigo (anchored on #1A1AF2 / accent #6C6AFF) --------
export const electric = {
  50: '#E9E9FE', // tint
  100: '#D6D6FD',
  200: '#B3B2FC',
  300: '#8E8DFF',
  400: '#6C6AFF', // ★ accent dk (secondary)
  500: '#4A48F7',
  600: '#1A1AF2', // ★ electric (primary)
  700: '#1414C4',
  800: '#1212A0',
  900: '#14146E',
  vivid: '#6C6AFF',
};
// Back-compat alias: components/theme still import `cobalt`.
export const cobalt = electric;

// The periwinkle secondary accent as a first-class token.
export const accent = { main: '#6C6AFF', soft: '#E9E9FE', dark: '#4A48F7' };

// --- Neutral: warm-grey ramp (Twin Console surfaces) ---------------------
export const neutral = {
  0: '#FFFFFF', //  surface
  25: '#FAFAF9',
  50: '#F4F4F2', //  warm light app ground
  100: '#EFEFEC',
  150: '#E6E6E2', //  hairline
  200: '#DEDEDA',
  300: '#CACAC5',
  400: '#9A9A97', //  muted / disabled
  500: '#6B6B68', //  secondary text
  600: '#4B4B49',
  700: '#2E2E2D',
  800: '#1A1A1C',
  900: '#0B0B0B', //  ink
  950: '#060606', //  near-black blocks
  black: '#000000',
};

// --- Semantic ------------------------------------------------------------
// info reuses the electric primary; warning reuses the 24K signature gold.
export const semantic = {
  success: { light: '#DCF7E8', main: '#16A34A', dark: '#15803D', contrast: '#FFFFFF' },
  warning: { light: '#FBF0CF', main: '#F5C542', dark: '#946A00', contrast: '#1C1606' },
  error: { light: '#FBE3E3', main: '#E5484D', dark: '#B5191F', contrast: '#FFFFFF' },
  info: { light: electric[50], main: electric[600], dark: electric[800], contrast: '#FFFFFF' },
};

// --- 24K signature gold (sparing brand accent) ---------------------------
export const signature = { light: '#FBF0CF', main: '#F5C542', dark: '#946A00', contrast: '#1C1606' };

// --- Data-visualisation categorical series -------------------------------
export const dataviz = {
  series: [
    electric[600], // electric primary
    neutral[900], // ink (comparison)
    accent.main, // periwinkle
    neutral[400], // muted grey
    semantic.success.main,
    signature.main,
    '#2DA8FF', // sky
    '#12B5A4', // teal
  ],
  positive: semantic.success.main,
  negative: semantic.error.main,
  neutral: neutral[400],
  reference: electric[600],
  gridLight: 'rgba(11,11,11,0.10)',
  gridDark: 'rgba(255,255,255,0.10)',
};

// --- Per-mode surface / text maps ----------------------------------------
export const surfaces = {
  light: {
    appBg: neutral[50], //  warm light ground
    paper: neutral[0], //  white cards
    paperAlt: neutral[25],
    sunken: neutral[100],
    inverse: neutral[950], //  ink "feature" blocks
    inverseText: '#F4F4F5',
    border: neutral[150], //  hairline
    borderStrong: neutral[300],
    text: {
      primary: neutral[900], //  ink
      secondary: '#5C5C5A',
      disabled: neutral[400],
      onAccent: '#FFFFFF',
      onInverse: '#F4F4F5',
    },
    accent: electric[600], //  #1A1AF2
    accentHover: electric[700],
    accentSoft: electric[50], //  #E9E9FE tint
    focusRing: 'rgba(26,26,242,0.32)',
    overlay: 'rgba(11,11,11,0.45)',
  },
  dark: {
    appBg: neutral[950], //  near-black page
    paper: '#121214', //  SURFACE DK
    paperAlt: '#1A1A1E',
    sunken: '#08080A',
    inverse: '#FFFFFF',
    inverseText: neutral[900],
    border: 'rgba(255,255,255,0.10)',
    borderStrong: 'rgba(255,255,255,0.20)',
    text: {
      primary: '#F4F4F5',
      secondary: 'rgba(244,244,245,0.62)',
      disabled: 'rgba(244,244,245,0.32)',
      onAccent: '#FFFFFF',
      onInverse: neutral[900],
    },
    accent: accent.main, //  #6C6AFF — lighter electric reads better on dark
    accentHover: electric[300],
    accentSoft: 'rgba(108,106,255,0.16)',
    focusRing: 'rgba(108,106,255,0.50)',
    overlay: 'rgba(0,0,0,0.60)',
  },
};

const palette = { electric, cobalt, accent, neutral, semantic, signature, dataviz, surfaces };
export default palette;
